import React, { useState } from 'react';
import { FaUpload, FaFile, FaFilePdf, FaFileWord, FaTrash, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';

const FileHandler = ({ 
    label = "Upload Files", 
    name, 
    value = [], 
    onChange, 
    accept = ".pdf,.doc,.docx", 
    multiple = true,
    maxFileSize = 10 * 1024 * 1024, // 10MB default
    maxFiles = 50,
    required = false,
    disabled = false,
    error
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Convert file to base64
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    // Validate file type
    const isValidFileType = (file) => {
        const validTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];
        return validTypes.includes(file.type);
    };

    // Get file icon based on type
    const getFileIcon = (fileType) => {
        if (fileType === 'application/pdf') {
            return <FaFilePdf className="text-red-500" />;
        } else if (fileType === 'application/msword' || fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            return <FaFileWord className="text-blue-500" />;
        }
        return <FaFile className="text-gray-500" />;
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Handle file selection
    const handleFileSelect = async (files) => {
        if (disabled) return;

        setUploading(true);
        const fileArray = Array.from(files);
        const currentFiles = Array.isArray(value) ? value : [];

        // Check max files limit
        if (currentFiles.length + fileArray.length > maxFiles) {
            toast.error(`You can only upload a maximum of ${maxFiles} files`);
            setUploading(false);
            return;
        }

        const processedFiles = [];

        for (const file of fileArray) {
            // Validate file type
            if (!isValidFileType(file)) {
                toast.error(`${file.name} is not a valid file type. Please upload PDF or Word documents only.`);
                continue;
            }

            // Validate file size
            if (file.size > maxFileSize) {
                toast.error(`${file.name} is too large. Maximum file size is ${formatFileSize(maxFileSize)}.`);
                continue;
            }

            try {
                const base64 = await fileToBase64(file);
                processedFiles.push({
                    id: Date.now() + Math.random(),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    base64: base64,
                    // Add redundant keys for server compatibility
                    file: base64,
                    content: base64,
                    uploadedAt: new Date().toISOString()
                });
            } catch (error) {
                toast.error(`Failed to process ${file.name}`);
                console.error('File processing error:', error);
            }
        }

        if (processedFiles.length > 0) {
            const updatedFiles = [...currentFiles, ...processedFiles];
            onChange(updatedFiles);
            toast.success(`${processedFiles.length} file(s) uploaded successfully`);
        }

        setUploading(false);
    };

    // Handle drag events
    const handleDragOver = (e) => {
        e.preventDefault();
        if (!disabled) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files);
        }
    };

    // Handle file input change
    const handleInputChange = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
            handleFileSelect(files);
        }
        // Reset input value to allow same file selection again
        e.target.value = '';
    };

    // Remove file
    const removeFile = (fileId) => {
        if (disabled) return;
        
        const currentFiles = Array.isArray(value) ? value : [];
        const updatedFiles = currentFiles.filter(file => file.id !== fileId);
        onChange(updatedFiles);
        toast.success('File removed successfully');
    };

    // Download file
    const downloadFile = (file) => {
        try {
            const link = document.createElement('a');
            link.href = file.base64;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            toast.error('Failed to download file');
            console.error('Download error:', error);
        }
    };

    const currentFiles = Array.isArray(value) ? value : [];

    return (
        <div className="flex flex-col gap-2">
            <label className="font-semibold text-gray-900 text-base">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            
            {/* Upload Area */}
            <div
                className={`
                    relative border-2 border-secondary border-dashed rounded-lg p-6 text-center transition-colors
                    ${isDragging ? 'border-primary bg-primary/5' : 'border-gray-300'}
                    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-primary hover:bg-primary/5 cursor-pointer'}
                    ${error ? 'border-red-500' : ''}
                `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !disabled && document.getElementById(`file-input-${name}`)?.click()}
            >
                <input
                    id={`file-input-${name}`}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={handleInputChange}
                    className="hidden"
                    disabled={disabled}
                />
                
                <div className="flex flex-col items-center gap-2">
                    <FaUpload className={`text-2xl ${disabled ? 'text-navBtn' : 'text-secondary'}`} />
                    <div className={`text-sm ${disabled ? 'text-navBtn' : 'text-gray-600'}`}>
                        {uploading ? (
                            <p>Processing files...</p>
                        ) : (
                            <>
                                <p>
                                    <span className="font-medium text-primary">Click to upload</span> or drag and drop
                                </p>
                                <p>PDF, DOC, DOCX files only</p>
                                <p className="text-xs">Maximum {formatFileSize(maxFileSize)} per file, up to {maxFiles} files</p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <p className="text-red-500 text-sm">{error}</p>
            )}

            {/* File List */}
            {currentFiles.length > 0 && (
                <div className="mt-4">
                    <h4 className="font-medium text-secondary mb-2">
                        Uploaded Files ({currentFiles.length}/{maxFiles})
                    </h4>
                    <div className="space-y-2">
                        {currentFiles.map((file) => (
                            <div
                                key={file.id}
                                className="flex items-center justify-between p-3 bg-border rounded-lg border"
                            >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {getFileIcon(file.type)}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-secondary truncate">
                                            {file.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formatFileSize(file.size)} • {new Date(file.uploadedAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => downloadFile(file)}
                                        className="p-1 text-chipBlue hover:text-blue-800 transition-colors"
                                        title="Download file"
                                    >
                                        <FaDownload className="text-sm" />
                                    </button>
                                    {!disabled && (
                                        <button
                                            type="button"
                                            onClick={() => removeFile(file.id)}
                                            className="p-1 text-red hover:text-red-800 transition-colors"
                                            title="Remove file"
                                        >
                                            <FaTrash className="text-sm" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileHandler;
